import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useUser } from '@vatom/sdk/react'

import { useSDK } from '../../../SDKProvider'

import {
  fetchMatrixPushers,
  fetchMatrixSync,
  fetchMatrixSyncNew,
  fetchMatrixUser,
  getMatrixUserDisplayName,
  setMatrixUserDisplayName
} from './api'
import {
  MatrixData,
  MatrixFullStateSyncOptions,
  MatrixPollStateSyncOptions,
  RoomInfo
} from './types'

export const matrixQueryKeys = {
  user: [{ scope: 'matrix-user' }] as const,
  spaces: [{ scope: 'matrix-spaces' }] as const,
  fullStateSync: [{ scope: 'matrix-full-state-sync' }] as const,
  pollStateSync: [{ scope: 'matrix-poll-state-sync' }] as const,
  getMatrixUser: (accessToken: string) => [{ ...matrixQueryKeys.user[0], accessToken }] as const,
  getSpaces: (businessId: string, communityId?: string) =>
    [{ ...matrixQueryKeys.spaces[0], businessId, communityId }] as const,
  getMatrixFullStateSync: (matrixToken?: string) =>
    [{ ...matrixQueryKeys.fullStateSync[0], matrixToken }] as const,
  getMatrixPollStateSync: (matrixToken: string, nextBatch: string) =>
    [{ ...matrixQueryKeys.pollStateSync[0], matrixToken, nextBatch }] as const
}

export const usePushers = (accessToken?: string, userId?: string) => {
  const setPushersQuery = useQuery({
    queryKey: ['matrix-pushers'],
    queryFn: () => fetchMatrixPushers(accessToken, userId),
    enabled: !!accessToken
  })
  return setPushersQuery
}

export const useMatrixUserDisplayName = (accessToken?: string, userId?: string) => {
  const getDisplayName = useQuery({
    queryKey: ['matrix-user-get-display-name', userId],
    queryFn: () => getMatrixUserDisplayName(accessToken, userId),
    enabled: !!accessToken
  })

  const user = useUser()
  const isSameDisplayName = getDisplayName.data?.displayname === user?.name

  const setDisplayName = useQuery({
    queryKey: ['matrix-user-set-display-name', userId],
    queryFn: () => setMatrixUserDisplayName(user?.name, accessToken, userId),
    enabled: !!accessToken && !isSameDisplayName && getDisplayName.isSuccess
  })
  return setDisplayName
}

export const useMatrixUser = () => {
  const sdk = useSDK()
  const userDataQuery = useQuery({
    queryKey: matrixQueryKeys.getMatrixUser(
      sdk.dataPool.sessionStore.vatomIncSessionToken?.accessToken ?? ''
    ),
    queryFn: fetchMatrixUser,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!sdk.dataPool.sessionStore.vatomIncSessionToken?.accessToken
  })

  // const userDisplayName = useMatrixUserDisplayName(
  //   userDataQuery.data?.access_token,
  //   userDataQuery.data?.user_id
  // )
  // const pushersData = usePushers(userDataQuery.data?.access_token, userDataQuery.data?.user_id)

  return userDataQuery
}

export const useGetMatrixFullStateSync = <T = MatrixData>(
  options?: MatrixFullStateSyncOptions<T>
) => {
  const result = useQuery({
    queryKey: matrixQueryKeys.getMatrixFullStateSync(''),
    ...options,
    enabled: false
  })

  return result
}

// This should only be called once
export const useMatrixFullStateSync = <T = MatrixData>(options?: MatrixFullStateSyncOptions<T>) => {
  const matrixUser = useMatrixUser()
  const result = useQuery({
    queryKey: matrixQueryKeys.getMatrixFullStateSync(''),
    queryFn: async () => {
      return fetchMatrixSync({
        token: matrixUser.data?.access_token
      })
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    ...options,
    enabled: !!matrixUser.data?.access_token && options?.enabled
  })

  const queryClient = useQueryClient()
  useMatrixPollSync({
    onSuccess: response => {
      const currentState = queryClient.getQueryState(matrixQueryKeys.getMatrixFullStateSync(''))
        ?.data as MatrixData | null

      if (response.rooms) {
        if ('join' in response.rooms) {
          for (const roomKey in response.rooms.join) {
            if (!currentState) {
              break
            }

            if (!currentState?.rooms) {
              // For a new room
              currentState.rooms = response.rooms
              break
            }

            if (!currentState?.rooms?.join?.[roomKey]) {
              // For a new room
              currentState.rooms.join[roomKey] = response.rooms.join[roomKey]
              break
            }

            const room = response.rooms.join[roomKey] as RoomInfo

            currentState.rooms.join[roomKey].timeline.events.push(...room.timeline.events)
          }
        }

        if ('leave' in response.rooms) {
          for (const roomKey in response.rooms.leave) {
            if (!currentState) {
              break
            }

            if (!currentState?.rooms.leave) {
              currentState.rooms['leave'] = {}
            }

            if (!currentState?.rooms.leave[roomKey]) {
              // For a new room
              currentState.rooms.leave[roomKey] = response.rooms.leave[roomKey]
              break
            }

            const room = response.rooms.leave[roomKey] as RoomInfo

            currentState.rooms.leave[roomKey].timeline.events.push(...room.timeline.events)
          }
        }
      }

      if (response.next_batch !== currentState?.next_batch) {
        queryClient.setQueryData(matrixQueryKeys.getMatrixFullStateSync(''), {
          ...currentState,
          next_batch: response.next_batch
        })
      } else {
        queryClient.invalidateQueries(matrixQueryKeys.pollStateSync)
      }
    }
  })
  return result
}

export const useMatrixPollSync = <T = MatrixData>(options?: MatrixPollStateSyncOptions<T>) => {
  const matrixUser = useMatrixUser()

  const currentFullState = useQuery<MatrixData>(matrixQueryKeys.getMatrixFullStateSync(''), {
    enabled: false
  })

  const result = useQuery({
    queryKey: matrixQueryKeys.getMatrixPollStateSync('', currentFullState.data?.next_batch ?? ''),
    queryFn: async ctx =>
      await fetchMatrixSyncNew({
        ...ctx,
        queryKey: matrixQueryKeys.getMatrixPollStateSync(
          matrixUser.data?.access_token ?? '',
          currentFullState.data?.next_batch ?? ''
        )
      }),
    ...options,
    enabled:
      !!matrixUser.data?.access_token && !!currentFullState.data?.next_batch && options?.enabled,
    cacheTime: 0
  })
  return result
}
